import {useUpmPlant} from '@hconnect/upm'
import {Button, ControlledInput, ControlledToggle} from '@hm/fundamental'
import {mdiLink} from '@mdi/js'
import {useCallback, useState} from 'react'
import {Controller, UseFormReturn} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {InstallationComments} from './InstallationComments'
import {InstallationDocuments} from './InstallationDocuments'
import {InstallationPerformanceChart} from './InstallationPerformanceChart'

import {ProductSelect} from '@refra/modules/common/components'
import {MaterialColorSelector} from '@refra/modules/common/components/Colors'
import {useCanEditColors} from '@refra/modules/common/hooks'
import {useUpdateRefractoryCustomization} from '@refra/modules/common/hooks/mutations'
import type {Refractory} from '@refra/modules/refra-catalog/types'
import {
  EditMaterialInstallationForm,
  MaterialInstallationDetails
} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

export interface GeneralDetailsTabProps {
  materialInstallationDetails: MaterialInstallationDetails
  formMethods: UseFormReturn<EditMaterialInstallationForm>
  isModalDisabled?: boolean
  min: number
  max: number
  assignedRefractory: Refractory
  shutdownId: number
  interventionDate: string
}

export const GeneralDetailsTab = ({
  shutdownId,
  interventionDate,
  materialInstallationDetails,
  formMethods,
  isModalDisabled,
  assignedRefractory,
  min,
  max
}: GeneralDetailsTabProps) => {
  const {t} = useTranslation()
  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})
  const navigate = useNavigate()
  const {mutate: updateCustomization} = useUpdateRefractoryCustomization()
  const [currentRefractory, setCurrentRefractory] = useState<Refractory | null>(assignedRefractory)

  const {control, formState, watch} = formMethods

  const currentFromMeter = watch('fromMeter')

  const canEditColors = useCanEditColors()

  const isModalFieldDisabled = isModalDisabled

  const handleColorChange = useCallback(
    (refractoryId: number, color: string) => {
      updateCustomization({
        plantUpmId: upmPlant.upmId,
        refractoryId,
        color
      })
    },
    [upmPlant.upmId, updateCustomization]
  )

  const handleColorReset = useCallback(
    (refractoryId: number) => {
      updateCustomization({
        plantUpmId: upmPlant.upmId,
        refractoryId,
        color: null
      })
    },
    [upmPlant.upmId, updateCustomization]
  )

  const handleRefractoryChange = useCallback((refractory: Refractory | null) => {
    setCurrentRefractory(refractory)
  }, [])

  return (
    <>
      <div className="space-y-4">
        <div className="flex">
          <Controller
            name="refractoryId"
            control={control}
            rules={{required: t('error.required')}}
            render={({field}) => (
              <ProductSelect
                activeAfter={interventionDate}
                plantUpmId={upmPlant.upmId}
                selectedValue={field.value}
                error={formState.errors.refractoryId?.message}
                isDisabled={isModalFieldDisabled}
                isTouched={!!formState.errors.refractoryId?.message}
                onChange={field.onChange}
                onRefractoryChange={handleRefractoryChange}
                initialSearchTerm={assignedRefractory.name.substring(0, 3)}
                fallbackRefractory={assignedRefractory}
                dataCy="update_material_product_select"
              />
            )}
          />
          <Button
            icon={mdiLink}
            variant="secondary"
            size="large"
            square
            onClick={() => void navigate(`/plants/${plantId}/catalog`)}
            data-cy="update_material_catalog_link_button"
          />
        </div>
        <div className="flex gap-4">
          <ControlledInput
            label={t('refraPlanning.materialInstallations.classification')}
            value={currentRefractory?.classification.class ?? ''}
            isReadOnly
            className="w-full"
            data-cy="update_material_classification_input"
          />
          <ControlledInput
            label={t('refraPlanning.materialInstallations.supplier')}
            value={currentRefractory?.manufacturer.name ?? ''}
            isReadOnly
            className="w-full"
            data-cy="update_material_supplier_input"
          />
        </div>
        <div className="flex gap-3">
          <Controller
            name="fromMeter"
            control={control}
            rules={{required: t('error.required')}}
            render={({field}) => (
              <ControlledInput
                {...field}
                label={t('refraPlanning.materialInstallations.fromMeter')}
                error={formState.errors.fromMeter?.message}
                isTouched={!!formState.touchedFields.fromMeter}
                isDisabled={isModalFieldDisabled}
                type={'number'}
                min={min}
                max={max}
                step={0.01}
                data-cy="update_material_from_meter_input"
                className="w-full"
              />
            )}
          />
          <Controller
            name="toMeter"
            control={control}
            rules={{
              required: t('error.required'),
              validate: (value) => Number(value) > Number(currentFromMeter) || t('error.toMeter')
            }}
            render={({field}) => (
              <ControlledInput
                {...field}
                label={t('refraPlanning.materialInstallations.toMeter')}
                error={formState.errors.toMeter?.message}
                isTouched={!!formState.touchedFields.toMeter}
                isDisabled={isModalFieldDisabled}
                type={'number'}
                min={min}
                max={max}
                step={0.01}
                data-cy="update_material_to_meter_input"
                className="w-full"
              />
            )}
          />
        </div>
        <div className="flex flex-col gap-4">
          {currentRefractory && (
            <MaterialColorSelector
              refractoryId={currentRefractory.id}
              globalColor={currentRefractory.color}
              localColor={currentRefractory.customization?.color ?? undefined}
              isDisabled={isModalFieldDisabled || !canEditColors}
              showGlobalColor={true}
              fullWidth={true}
              showTitles={true}
              onColorChange={handleColorChange}
              onColorReset={handleColorReset}
              dataCy="update_material_color_selector"
            />
          )}
        </div>
        <div className="flex flex-col gap-4">
          <Controller
            name="isMortarRequired"
            control={control}
            render={({field}) => (
              <ControlledToggle
                {...field}
                label={t('refraPlanning.materialInstallations.mortar')}
                checked={Boolean(field.value)}
                value={undefined}
                disabled={isModalFieldDisabled}
                data-cy="update_material_is_mortar_switch"
              />
            )}
          />
        </div>
      </div>
      <InstallationPerformanceChart materialInstallation={materialInstallationDetails} />
      {materialInstallationDetails !== undefined && (
        <>
          <InstallationDocuments
            materialInstallationDetails={materialInstallationDetails}
            shutdownId={shutdownId}
            isReadOnly={isModalDisabled}
          />
          <InstallationComments
            materialInstallationDetails={materialInstallationDetails}
            isReadOnly={isModalDisabled}
          />
        </>
      )}
    </>
  )
}
